import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useOutletContext } from "react-router"
import { Copy, DotsThreeVertical, PencilSimple, Trash } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button.tsx"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx"
import { AgendaAction } from "@/modules/patients/agenda"
import { TimeWindowDeleteDialog } from "@/modules/patients/agenda/time-window/components/time-window-delete-dialog"
import { TimeWindowRestrictedDialog } from "@/modules/patients/agenda/time-window/components/time-window-restricted-dialog"
import { DELETE_RESTRICTION_TIMEOUT } from "@/modules/patients/constants/Agenda"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { AppointmentAgenda } from "@/modules/patients/libs/categorize-date-ranges"
import { isTimeWindowDeletionRestricted } from "@/modules/patients/libs/time-window-deletion-restriction"
import { useSwissTimeWindowDeleteMutation } from "@/modules/patients/services/ch/useChDeleteTimeWindow.ts"

interface AgendaTableMenuProps {
  onHandleRowClick: (timeWindow: AppointmentAgenda, mode: AgendaAction) => void
  rowData: AppointmentAgenda
}
const AgendaTableMenu = ({ onHandleRowClick, rowData }: AgendaTableMenuProps) => {
  const patientId = usePatientParam()
  const user = useUser()
  const parsedPatientId: string = patientId || ""
  const { isEditMode } = useOutletContext() as { isEditMode: boolean }
  const { t, i18n } = useTranslation()
  const { deleteTimeWindow } = useSwissTimeWindowDeleteMutation(parsedPatientId, i18n.language)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const [onDeleteRestrictionOpen, setOnDeleteRestrictionOpen] = useState(false)
  const isCustomerServiceCenter = user.role === "CustomerServiceCenter"

  const deletionRestrictionParams = {
    limit: DELETE_RESTRICTION_TIMEOUT,
    isCustomerServiceCenter,
    createdAt: new Date(rowData.createdAt!),
  }

  const canEditTimeWindow = isCustomerServiceCenter || !rowData.errorCorrection

  const onInvalid = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setConfirmDialogOpen(false)
    setOnDeleteRestrictionOpen(true)
    event.stopPropagation()
  }

  const onValid = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setConfirmDialogOpen(true)
    event.stopPropagation()
  }

  const onDelete = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    /*
       Even when pressing confirmation, the restriction is re-checked first because the dialog
       could stay open long enough for the time window to cross the deletion limit.
    */
    if (isTimeWindowDeletionRestricted(deletionRestrictionParams)) {
      onInvalid(event)
      return
    }

    event.stopPropagation()
    setConfirmDialogOpen(false)
    deleteTimeWindow({ id: rowData.id })
  }

  if (isEditMode) {
    return (
      <>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild className="outline-hidden">
            <Button
              data-testid="action-button"
              variant="circle"
              className="action-button absolute top-0.5 right-0.75 h-9 w-9 p-0 outline-hidden!"
              onClick={(event) => event.stopPropagation()}>
              <DotsThreeVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            hideWhenDetached
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            align="end"
            data-testid="agenda-options-content"
            className="min-w-50 rounded-lg border-none p-2 leading-5 transition-all duration-200">
            <>
              {canEditTimeWindow && (
                <>
                  <DropdownMenuCheckboxItem
                    className="text-on-surface"
                    data-testid="agenda-edit-option"
                    onClick={(event) => {
                      onHandleRowClick(rowData, "EDIT")
                      event.stopPropagation()
                    }}>
                    <PencilSimple className="mr-3" /> {t("patient.agenda.grid.options.edit")}
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator className="-mx-2 mt-2 mb-0" />
                </>
              )}
              <DropdownMenuCheckboxItem
                className="text-on-surface"
                data-testid="agenda-copy-option"
                onClick={(event) => {
                  onHandleRowClick(rowData, "COPY")
                  event.stopPropagation()
                }}>
                <Copy className="mr-3" /> {t("patient.agenda.grid.options.copy")}
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator className="-mx-2 mt-2 mb-0" />
              {!isTimeWindowDeletionRestricted(deletionRestrictionParams) && (
                <>
                  <DropdownMenuCheckboxItem
                    className="text-on-surface"
                    data-testid="agenda-delete-option"
                    onClick={(event) => {
                      if (isTimeWindowDeletionRestricted(deletionRestrictionParams)) onInvalid(event)
                      else onValid(event)
                    }}>
                    <Trash className="mr-3" /> {t("patient.agenda.grid.options.delete")}
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator className="-mx-2 mt-2 mb-0" />
                </>
              )}
            </>
          </DropdownMenuContent>
        </DropdownMenu>
        <TimeWindowRestrictedDialog
          open={onDeleteRestrictionOpen}
          onConfirm={(event) => {
            event.stopPropagation()
            setOnDeleteRestrictionOpen(false)
          }}
        />
        <TimeWindowDeleteDialog
          open={confirmDialogOpen}
          onDiscard={(event) => onDelete(event)}
          onConfirm={(event) => {
            event.stopPropagation()
            setConfirmDialogOpen(false)
          }}
        />
      </>
    )
  }

  return null
}

export { AgendaTableMenu }
